/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import "antd/dist/antd.css";
import "css/touchpointManager.css";
import {
  Input,
  Tooltip,
  Table,
  Button,
  Form,
  Modal,
  Drawer,
  message,
} from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  EditOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { testJourneyTouchpoint } from "../../apiService/RESTApi";
import { journeys, useBlustreamApiMutation } from "../../apiService/blustreamApi";
import { touchpointFromBeta } from "./adapters";
import successIcon from "../../assets/Images/successIcon.svg";
import ContactSelector from "../../Components/Common/ContactSelector";

const { TextArea } = Input;
const SUCCESS_ICON_HOLD_MS = 1500;

export default function TableView(props) {
  const { roAdmin } = useSelector((state) => state.loginReducer);
  // Two form instances — the edit-touchpoint drawer and the edit-journey drawer
  // both bind a Form.Item to `messageContent` (the journey drawer reuses it for
  // its state-button group). Sharing one form instance bleeds field values
  // between drawers; keep them isolated.
  const [touchpointForm] = Form.useForm();
  const [journeyEditForm] = Form.useForm();

  const [edit, setEdit] = useState(false);
  const [editJourney, setEditJourney] = useState(false);
  const [journeyState, setJourneyState] = useState('live');
  const [journeyForm, setJourneyForm] = useState({});
  const [touchPointForm, setTouchPointForm] = useState({});
  const [touchpointBeingEdited, setTouchpointBeingEdited] = useState();

  // Optimistic state: pending field merges + per-row UI status
  const [optimisticById, setOptimisticById] = useState({}); // { [tid]: { messageContent, contentLinkToTrackableLink, ... } }
  const [statusById, setStatusById] = useState({}); // { [tid]: 'pending' | 'success' | 'error' }

  const [isContactSelectorOpen, setIsContactSelectorOpen] = useState(false);
  const [isTouchpointSentModalOpen, setIsTouchpointSentModalOpen] = useState(false);
  const [testJourneyId, setTestJourneyId] = useState("");
  const [testTouchPointId, setTestTouchPointId] = useState("");

  const journey = props.journey;
  const touchpoints = props.touchpoints || [];
  const journeyId = props.journeyId;
  const onTouchpointUpdated = props.onTouchpointUpdated;

  const [patch] = useBlustreamApiMutation((jid, tid, body, signal) =>
    journeys.updateTouchpoint(jid, tid, body, { signal })
  );

  // Reset per-journey edit state when journey changes
  useEffect(() => {
    setOptimisticById({});
    setStatusById({});
    setEdit(false);
  }, [journeyId]);

  // Apply optimistic merges over canonical touchpoints from parent
  const mergedTouchpoints = useMemo(
    () => touchpoints.map((t) => ({ ...t, ...(optimisticById[t.id] || {}) })),
    [touchpoints, optimisticById]
  );

  const renderContent = (value, row) => {
    const obj = { children: value, props: {} };
    if (row?.name === true) obj.props.colSpan = 0;
    return obj;
  };

  const renderEditCell = (value, row) => {
    if (row?.name === true) {
      // Banner row — keep journey edit (unchanged, still gandalf-bound)
      return {
        children: (
          <div className="edit-journey-wrapper">
            <Button
              disabled={roAdmin}
              type="text"
              style={{ color: "#959595" }}
              onClick={() => handleEditJourney(row)}
            >
              <EditOutlined style={{ fontSize: "20px" }} />
            </Button>
          </div>
        ),
      };
    }
    return value;
  };

  const columns = [
    {
      title: "Touchpoint Name",
      dataIndex: "Tname",
      key: "Tname",
      className: "touchpoint-name ",
      render: (text, row) => {
        if (row?.name === true) {
          const stateLabel = row?.journey?.currentState?.state;
          const journeyInfo = stateLabel
            ? `Journey: ${text} - (${stateLabel})`
            : `Journey: ${text}`;
          return {
            children: (
              <div className="name-wrapper p-16">
                <span style={{ color: "#124173", fontWeight: 600 }}>
                  {journeyInfo}
                </span>
              </div>
            ),
            props: { colSpan: 4 },
          };
        }
        return <span className="p-16 d-block">{text}</span>;
      },
    },
    {
      title: "Trigger/Timing",
      dataIndex: "type",
      key: "type",
      className: "p-16",
      render: renderContent,
    },
    {
      title: "Message",
      key: "msg",
      dataIndex: "msg",
      className: "p-16",
      render: renderContent,
    },
    {
      title: "Links",
      key: "link",
      dataIndex: "link",
      className: "p-16",
      render: renderContent,
    },
    {
      title: "Edit",
      dataIndex: "edit",
      key: "edit",
      className: "edit-journey-name",
      width: 80,
      render: renderEditCell,
    },
  ];

  const handleEdit = (data) => {
    let temp = { ...touchPointForm };
    temp["id"] = data?.id;
    temp["ordinal"] = data?.ordinal;
    temp["wait"] = data?.wait || "Immediate on insert";
    temp["name"] = data?.name;
    temp["messageContent"] = data?.messageContent || "";
    temp["messageTemplateName"] = data?.messageTemplateName;
    temp["contentLinkToTrackableLink"] =
      data?.contentLinkToTrackableLink && typeof data.contentLinkToTrackableLink === 'object'
        ? Object.keys(data.contentLinkToTrackableLink)[0] || ""
        : (data?.contentLinkToTrackableLink || "");
    setTouchpointBeingEdited(data?.id);
    touchpointForm.setFieldsValue(temp);
    setTouchPointForm(temp);

    // Clear any persistent error icon for this row when reopened
    setStatusById((prev) => {
      if (prev[data?.id] !== 'error') return prev;
      const next = { ...prev };
      delete next[data?.id];
      return next;
    });

    setEdit(true);
  };

  const handleEditJourney = (item) => {
    let journey = item["journey"];
    let temp = { ...journeyForm };
    temp["journeyName"] = journey?.name;
    temp["journeyId"] = journey?.id;
    temp["currentState"] = journey?.currentState?.state;

    journeyEditForm.setFieldsValue(temp);
    setJourneyForm(temp);
    setState(journey?.currentState?.state?.toLowerCase() || 'live');
    setEditJourney(true);
  };

  const onClose = () => {
    if (edit === true) {
      setEdit(false);
    } else {
      setEditJourney(false);
    }
  };

  const handleSaveTouchpoint = async () => {
    const fieldValues = touchpointForm.getFieldsValue();
    const tid = touchpointBeingEdited;
    if (!tid) return;

    const nextMessage = fieldValues.messageContent ?? "";
    const nextLink = fieldValues.contentLinkToTrackableLink ?? "";

    // Optimistic merge — render reflects pending value immediately
    setOptimisticById((prev) => ({
      ...prev,
      [tid]: {
        messageContent: nextMessage,
        contentLinkToTrackableLink: nextLink ? { [nextLink]: true } : null,
      },
    }));
    setStatusById((prev) => ({ ...prev, [tid]: 'pending' }));
    setEdit(false);

    try {
      const updatedBeta = await patch(journeyId, tid, {
        message: nextMessage,
        link: nextLink || null,
      });
      const updatedLegacy = touchpointFromBeta(updatedBeta, journeyId);

      // Promote to canonical via parent cache; drop optimistic + flash success
      if (onTouchpointUpdated) {
        onTouchpointUpdated(journeyId, tid, updatedLegacy);
      }
      setOptimisticById((prev) => {
        const next = { ...prev };
        delete next[tid];
        return next;
      });
      setStatusById((prev) => ({ ...prev, [tid]: 'success' }));
      setTimeout(() => {
        setStatusById((prev) => {
          if (prev[tid] !== 'success') return prev;
          const next = { ...prev };
          delete next[tid];
          return next;
        });
      }, SUCCESS_ICON_HOLD_MS);
    } catch (err) {
      if (err?.suppressed || err?.name === 'AbortError') return;
      // Roll back + persistent error icon
      setOptimisticById((prev) => {
        const next = { ...prev };
        delete next[tid];
        return next;
      });
      setStatusById((prev) => ({ ...prev, [tid]: 'error' }));
      message.error("Touchpoint failed to save. Please try again.");
    }
  };

  const renderEditButton = useCallback((value) => {
    const status = statusById[value?.id];
    const disabled = roAdmin || status === 'pending';
    let icon;
    if (status === 'pending') {
      icon = <LoadingOutlined style={{ fontSize: "20px", color: "#959595" }} />;
    } else if (status === 'success') {
      icon = <CheckCircleFilled style={{ fontSize: "20px", color: "#52c41a" }} />;
    } else if (status === 'error') {
      icon = <CloseCircleFilled style={{ fontSize: "20px", color: "#ff4d4f" }} />;
    } else {
      icon = <EditOutlined style={{ fontSize: "20px" }} />;
    }

    const button = (
      <Button
        disabled={disabled}
        type="text"
        style={{ color: "#959595" }}
        onClick={() => handleEdit(value)}
      >
        {icon}
      </Button>
    );

    if (status === 'error') {
      return (
        <Tooltip title="Save failed. Click to edit and try again.">{button}</Tooltip>
      );
    }
    return button;
  }, [statusById, roAdmin]);

  const tableData = useMemo(() => {
    if (!journey) return [];
    const rows = [];
    rows.push({ Tname: journey.name, name: true, journey });
    mergedTouchpoints.forEach((value) => {
      rows.push({
        key: value?.id ?? "",
        journeyId: journey.journey_id,
        Tname: (
          <>
            <p>{value?.messageTemplateName || ""}</p>
            <button
              disabled={roAdmin}
              style={{
                textDecoration: "underline",
                fontStyle: "italic",
                color: "#F37064",
                cursor: "pointer",
                border: "none",
                background: "none",
              }}
              onClick={() => onTestNowClicked(journey.journey_id, value.id)}
            >
              Test now
            </button>
          </>
        ),
        edit: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {renderEditButton(value)}
          </div>
        ),
        type: (
          <p>
            {value?.wait !== undefined && value?.wait !== null && value.wait !== ""
              ? moment.duration(value.wait).asDays() >= 1
                ? `After ${moment.duration(value.wait).asDays()} days to previous`
                : `After ${moment.duration(value.wait).asHours()} hours to previous`
              : "Immediate on insert"}
          </p>
        ),
        msg:
          value?.messageContent !== undefined ? (
            <pre style={{ whiteSpace: "break-spaces", fontFamily: "Hellix" }}>
              {value.messageContent}
            </pre>
          ) : (
            ""
          ),
        link:
          value?.contentLinkToTrackableLink ? (
            <a
              href={Object.keys(value.contentLinkToTrackableLink)[0]}
              target="_blank"
              rel="noreferrer"
            >
              {Object.keys(value.contentLinkToTrackableLink)[0]}
            </a>
          ) : (
            ""
          ),
      });
    });
    return rows;
  }, [journey, mergedTouchpoints, renderEditButton, roAdmin]);

  const selectContactsHandleOk = async (selectedContacts) => {
    // eslint-disable-next-line
    selectedContacts.map(async (contactId) => {
      return await testJourneyTouchpoint(contactId, testJourneyId, testTouchPointId);
    });
  };

  const selectContactsHandleCancel = () => {};

  const onTestNowClicked = (jid, touchpointId) => {
    setIsContactSelectorOpen(true);
    setTestJourneyId(jid);
    setTestTouchPointId(touchpointId);
  };

  const setState = (newState) => {
    setJourneyState(newState);
  };

  return (
    <>
      <div className="tableView">
        <Table
          className="journey-table"
          columns={columns}
          loading={false}
          pagination={false}
          tableLayout="fixed"
          bordered
          dataSource={tableData}
          scroll={{ y: 450 }}
        />
        <Drawer
          width={430}
          title="Edit Journey"
          placement="right"
          onClose={onClose}
          open={editJourney}
        >
          <Form
            form={journeyEditForm}
            layout="vertical"
            className="edit-journey-form"
            style={{ paddingBottom: 60 }}
          >
            <Form.Item
              label="Journey Name"
              style={{ width: "100%" }}
              name="journeyName"
            >
              <Input
                className="standard-input"
                disabled={true}
                placeholder="Enter Journey Name"
              />
            </Form.Item>
            <Form.Item
              label={
                <Tooltip
                  placement="rightBottom"
                  title={
                    <>
                      <strong>Live:</strong> Sending messages to all eligible contacts.<br />
                      <strong>Muted:</strong> Continue the journey's schedule but silence all outbound messages and responses.<br />
                      <strong>Paused:</strong> Temporarily stop the schedule and resume from current position when you leave this state.<br />
                      <strong>Disabled:</strong> Stop all messaging and reset all journeys. Journeys will restart from the beginning for all eligible contacts when resuming.
                    </>
                  }
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div>State</div>
                    <InfoCircleOutlined style={{ paddingLeft: "3px" }} />
                  </div>
                </Tooltip>
              }
              style={{ width: "100%" }}
              name="journeyStateButtons"
              type="textarea"
            >
              <div className="journey-state-switcher">
                <Button className={`disabled journey-state-button ant-btn ant-btn-secondary standard-button ${journeyState === 'live' ? "active primary-orange-button" : ""}`}>Live</Button>
                <Button className={`disabled journey-state-button ant-btn ant-btn-secondary standard-button ${journeyState === 'muted' ? "active primary-orange-button" : ""}`}>Muted</Button>
                <Button className={`disabled journey-state-button ant-btn ant-btn-secondary standard-button ${journeyState === 'paused' ? "active primary-orange-button" : ""}`}>Paused</Button>
                <Button className={`disabled journey-state-button ant-btn ant-btn-secondary standard-button ${journeyState === 'disabled' ? "active primary-orange-button" : ""}`}>Disabled</Button>
              </div>
            </Form.Item>
            <div className="action-buttons-wrapper">
              <Button
                className="standard-button tertiary-button"
                style={{ marginRight: 8, width: "48%" }}
                size="middle"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                style={{ width: "48%" }}
                htmlType="submit"
                className="standard-button primary-orange-button"
                size="middle"
              >
                Save
              </Button>
            </div>
          </Form>
        </Drawer>

        <Drawer
          width={430}
          title="Edit touchpoint details"
          placement="right"
          onClose={onClose}
          open={edit}
        >
          <Form
            form={touchpointForm}
            layout="vertical"
            className="edit-touchpoint-form"
            style={{ paddingBottom: 60 }}
            onFinish={handleSaveTouchpoint}
          >
            <Form.Item
              label="Touchpoint Name"
              style={{ width: "100%" }}
              name="messageTemplateName"
            >
              <Input
                className="standard-input"
                disabled={true}
                placeholder="Enter Touchpoint Name"
              />
            </Form.Item>
            <Form.Item
              label="Trigger/Timing"
              style={{ width: "100%" }}
              name="wait"
            >
              <Input className="standard-input" disabled={true} />
            </Form.Item>
            <Form.Item
              label={
                <Tooltip
                  placement="rightBottom"
                  title="To insert the link from the field below into the message, add {{LINK}} to the message’s text."
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div>Message</div>
                    <InfoCircleOutlined style={{ paddingLeft: "3px" }} />
                  </div>
                </Tooltip>
              }
              style={{ width: "100%" }}
              name="messageContent"
              type="textarea"
            >
              <TextArea
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder="Enter Message"
              />
            </Form.Item>
            <Form.Item
              label={
                <Tooltip
                  placement="rightBottom"
                  title="Links added here become trackable automatically."
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div>Link</div>
                    <InfoCircleOutlined style={{ paddingLeft: "3px" }} />
                  </div>
                </Tooltip>
              }
              style={{ width: "100%" }}
              name="contentLinkToTrackableLink"
            >
              <Input className="standard-input" placeholder="Enter Link" />
            </Form.Item>
            <div className="action-buttons-wrapper">
              <Button
                className="standard-button tertiary-button"
                style={{ marginRight: 8, width: "48%" }}
                size="middle"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                style={{ width: "48%" }}
                htmlType="submit"
                className="standard-button primary-orange-button"
                size="middle"
              >
                Save
              </Button>
            </div>
          </Form>
        </Drawer>
      </div>
      <ContactSelector
        isOpen={isContactSelectorOpen}
        toggleModal={() => setIsContactSelectorOpen(!isContactSelectorOpen)}
        allowMultipleSelection={true}
        footerText={`Clicking Start Journey will start the selected customers on a journey. This action may trigger future messages.`}
        buttonText="Start Journey"
        onOkCallback={selectContactsHandleOk}
        onCancelCallback={selectContactsHandleCancel}
      />
      <Modal
        open={isTouchpointSentModalOpen}
        onCancel={() => setIsTouchpointSentModalOpen(false)}
        footer={null}
        centered
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "30px 40px",
            gap: "22px",
          }}
        >
          <img src={successIcon} alt="" />
          <p
            style={{
              fontWeight: "600",
              fontSize: "22px",
              lineHeight: "30px",
              color: "#252525",
            }}
          >
            Touchpoint Sent !
          </p>
        </div>
      </Modal>
    </>
  );
}
